// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

//! Local vault mapping — tracks the relationship between server-assigned
//! vault IDs and BIP44 derivation indices, scoped by user fingerprint.
//!
//! With the master-seed model, each vault's keys are derived from the
//! user's master seed at a specific account index. The mapping also
//! caches display fields so the vault list renders without N fetches.

/** The localStorage key this module owns. Exported (COB-500) so the browser
 *  wipe can name it in its report without a second copy of the string. */
export const VAULT_STORE_KEY = 'cobault_vaults';

const STORAGE_KEY = VAULT_STORE_KEY;

export interface VaultMapping {
  serverVaultId: string;
  derivationIndex: number;
  userFingerprint: string;
  // Cached display fields — populated at vault creation.
  state?: string;
  principalSats?: number;
  depositAddress?: string;
  createdAtUnix?: number;
}

function readMappings(): VaultMapping[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

/** Save a vault mapping scoped to a user. */
export function saveVaultMapping(
  serverVaultId: string,
  derivationIndex: number,
  userFingerprint: string,
  display?: {
    state?: string;
    principalSats?: number;
    depositAddress?: string;
    createdAtUnix?: number;
  },
): void {
  const mappings = readMappings();
  mappings.push({
    serverVaultId,
    derivationIndex,
    userFingerprint,
    ...display,
  });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(mappings));
}

/** COB-462: record a vault's deposit address once it is revealed.
 *
 *  Since COB-245 the address does not exist at creation time — it is withheld
 *  until the owner confirms the contract on the independent verifier — so the
 *  mapping is written with `depositAddress: undefined` and filled in here at
 *  acknowledgement. A no-op for an unknown vault, and never overwrites a value
 *  already stored (the address is fixed for the life of the vault; a differing
 *  one is a server-side change we do not want to silently adopt into the local
 *  cache). Display-only data — nothing signs against it. */
export function rememberVaultDepositAddress(
  serverVaultId: string,
  depositAddress: string,
): void {
  const mappings = readMappings();
  const entry = mappings.find(m => m.serverVaultId === serverVaultId);
  if (!entry || entry.depositAddress) return;
  entry.depositAddress = depositAddress;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(mappings));
}

/** Get the derivation index for a given server vault ID. */
export function getDerivationIndex(serverVaultId: string): number | null {
  return readMappings().find(m => m.serverVaultId === serverVaultId)?.derivationIndex ?? null;
}

/** Get all vault mappings belonging to a specific user. */
export function getMyVaults(userFingerprint: string): VaultMapping[] {
  return readMappings().filter(m => m.userFingerprint === userFingerprint);
}

/** COB-500: drop every mapping belonging to one user fingerprint — the
 *  local-vault-store half of "Flush browser stored key material".
 *
 *  Scoped by fingerprint, never by "clear the key": other accounts on this
 *  browser keep their rows, and the whole entry is removed only when nothing
 *  of anyone else's is left (so the wiped browser looks untouched rather
 *  than carrying an empty array). Returns how many rows were dropped. */
export function forgetVaultsForFingerprint(userFingerprint: string): number {
  const mappings = readMappings();
  const kept = mappings.filter(m => m.userFingerprint !== userFingerprint);
  const dropped = mappings.length - kept.length;
  if (dropped === 0) return 0;
  if (kept.length === 0) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
  }
  return dropped;
}

/** Get all known user fingerprints (for multi-user awareness). */
export function getKnownFingerprints(): string[] {
  const seen = new Set<string>();
  for (const m of readMappings()) {
    if (m.userFingerprint) seen.add(m.userFingerprint);
  }
  return [...seen];
}
